import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import { hoyIso, type Obligacion, type Registro } from "./tipos"

/**
 * Registrar una presentación, declaración o solvencia, con su soporte.
 *
 * ⚠ **El vencimiento lo PROPONE el servidor** a partir de la fecha y la periodicidad
 * (`/propuesta-vence`), y se puede cambiar: manda lo que diga el papel. Mientras la
 * persona no lo toque, se vuelve a proponer al cambiar la fecha.
 *
 * ⚠ **En una solvencia o licencia el vencimiento es obligatorio**: sin él no se sabe
 * hasta cuándo se está solvente.
 */
export function ModalRegistro({
  obligacion,
  onCerrar,
}: {
  obligacion: Obligacion
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [periodo, setPeriodo] = useState("")
  const [fecha, setFecha] = useState(hoyIso())
  const [numero, setNumero] = useState("")
  const [vence, setVence] = useState("")
  const [venceTocado, setVenceTocado] = useState(false)
  const [observacion, setObservacion] = useState("")
  const [soporte, setSoporte] = useState<File | null>(null)

  const propuesta = useQuery({
    queryKey: ["cumplimiento", "propuesta", obligacion.id, fecha],
    queryFn: () =>
      api.get<{ vence: string | null }>(
        `/api/v1/cumplimiento/obligaciones/${obligacion.id}/propuesta-vence?fecha=${fecha}`,
      ),
    enabled: fecha.length === 10,
  })
  useEffect(() => {
    if (!venceTocado && propuesta.data) setVence(propuesta.data.vence ?? "")
  }, [propuesta.data, venceTocado])

  const guardar = useMutation({
    mutationFn: () => {
      const datos = new FormData()
      datos.append("periodo", periodo)
      datos.append("fecha", fecha)
      if (numero.trim()) datos.append("numero", numero.trim())
      if (vence) datos.append("vence", vence)
      if (observacion.trim()) datos.append("observacion", observacion.trim())
      if (soporte) datos.append("soporte", soporte)
      return api.post<Registro>(
        `/api/v1/cumplimiento/obligaciones/${obligacion.id}/registros`,
        datos,
      )
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cumplimiento"] })
      onCerrar()
    },
    meta: { exito: "Registro guardado" },
  })

  const exigeVence = obligacion.periodicidad === "POR_VIGENCIA"
  const listo = periodo.trim() !== "" && fecha !== "" && (!exigeVence || vence !== "")

  return (
    <Modal
      abierto={true}
      titulo={`Registrar · ${obligacion.nombre}`}
      descripcion="Lo que se presentó, pagó u obtuvo, con su comprobante."
      onCerrar={onCerrar}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="reg-periodo" className="mb-1 block text-xs font-medium uppercase">
            Período que cubre
          </label>
          <input
            id="reg-periodo"
            className="campo"
            placeholder="2026-09, 3.er trimestre 2026…"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="reg-numero" className="mb-1 block text-xs font-medium uppercase">
            Número de planilla o certificado
          </label>
          <input
            id="reg-numero"
            className="campo"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="reg-fecha" className="mb-1 block text-xs font-medium uppercase">
            Fecha de presentación o emisión
          </label>
          <input
            id="reg-fecha"
            type="date"
            className="campo tabular-nums"
            max={hoyIso()}
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="reg-vence" className="mb-1 block text-xs font-medium uppercase">
            {exigeVence ? "Vence (obligatorio)" : "Vence o toca la siguiente"}
          </label>
          <input
            id="reg-vence"
            type="date"
            className="campo tabular-nums"
            min={fecha}
            value={vence}
            onChange={(e) => {
              setVenceTocado(true)
              setVence(e.target.value)
            }}
          />
          {!venceTocado && propuesta.data?.vence && (
            <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Propuesto según la periodicidad; corrígelo si el papel dice otra cosa.
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="reg-obs" className="mb-1 block text-xs font-medium uppercase">
            Observación
          </label>
          <textarea
            id="reg-obs"
            rows={2}
            className="campo"
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="reg-soporte" className="mb-1 block text-xs font-medium uppercase">
            Soporte (PDF o imagen)
          </label>
          <input
            id="reg-soporte"
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            className="text-sm"
            onChange={(e) => setSoporte(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>

      {guardar.error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          variante="primario"
          disabled={!listo || guardar.isPending}
          onClick={() => guardar.mutate()}
        >
          Guardar
        </Boton>
      </div>
    </Modal>
  )
}
